import { useState, useEffect, useRef } from 'react';
import { X, Shield, AlertTriangle, DollarSign, Info, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import { useLocale } from '../../contexts/LocaleContext';

/* ─── Shared slider ─── */
function NativeSlider({ value, min, max, step, onChange, accentVar = '--accent-primary, #3b82f6' }) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="relative flex items-center h-10">
      <div className="absolute left-0 right-0 h-[5px] rounded-full"
        style={{ background: 'var(--bg-tertiary, #e5e7eb)' }} />
      <div className="absolute left-0 h-[5px] rounded-full"
        style={{ width: `${pct}%`, background: `var(${accentVar})`, transition: 'width .05s' }} />
      <input type="range" min={min} max={max} step={step} value={value} onChange={onChange}
        className="ov-thumb w-full h-10 absolute left-0 cursor-pointer bg-transparent appearance-none" />
    </div>
  );
}

/* ─── Editable value badge ─── */
function EditableBadge({ displayValue, rawValue, min, max, step, onCommit }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);

  const startEdit = () => {
    setDraft(rawValue !== null && rawValue !== '' ? String(rawValue) : '');
    setEditing(true);
    setTimeout(() => { inputRef.current?.select(); }, 0);
  };

  const commit = () => {
    const parsed = step % 1 === 0 ? parseInt(draft) : parseFloat(draft);
    if (!isNaN(parsed)) {
      const clamped = Math.min(max, Math.max(min, parsed));
      onCommit(clamped);
    }
    setEditing(false);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter') commit();
    if (e.key === 'Escape') setEditing(false);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="number"
        min={min} max={max} step={step}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKey}
        className="text-sm font-mono font-semibold px-2 py-0.5 rounded-full text-center w-24 outline-none"
        style={{
          background: 'color-mix(in srgb, var(--accent-primary,#3b82f6) 10%, transparent)',
          color: 'var(--accent-primary, #3b82f6)',
          border: '1.5px solid var(--accent-primary, #3b82f6)',
        }}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={startEdit}
      title="Click to type a value"
      className="text-sm font-mono font-semibold px-2 py-0.5 rounded-full transition-all active:scale-95"
      style={{
        background: 'color-mix(in srgb, var(--accent-primary,#3b82f6) 10%, transparent)',
        color: 'var(--accent-primary, #3b82f6)',
        border: '1.5px solid transparent',
        cursor: 'text',
      }}>
      {displayValue}
    </button>
  );
}

function SliderField({ label, hint, value, displayValue, sliderValue, min, max, step, onChange, onReset, accentVar, resetLabel, onDirectInput }) {
  return (
    <div className="py-3">
      <div className="flex items-center justify-between mb-1">
        <label className="text-[15px] font-medium" style={{ color: 'var(--text-primary, #111)' }}>{label}</label>
        <EditableBadge
          displayValue={displayValue}
          rawValue={value}
          min={min} max={max} step={step}
          onCommit={onDirectInput}
        />
      </div>
      <NativeSlider value={sliderValue} min={min} max={max} step={step} onChange={onChange} accentVar={accentVar} />
      {onReset && (
        <button type="button" onClick={onReset}
          className="mt-1 text-xs font-medium underline underline-offset-2 active:opacity-60"
          style={{ color: 'var(--accent-info, #0ea5e9)' }}>
          {resetLabel}
        </button>
      )}
      {hint && <p className="mt-1 text-xs" style={{ color: 'var(--text-secondary, #6b7280)' }}>{hint}</p>}
    </div>
  );
}

function ToggleRow({ checked, onChange, label, sublabel, danger }) {
  return (
    <>
      <div className="ov-toggle-row flex items-center justify-between py-4">
        <div>
          <p className="text-[15px] font-medium"
            style={{ color: danger ? 'var(--accent-danger, #ef4444)' : 'var(--text-primary, #111)' }}>
            {label}
          </p>
          {sublabel && <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary, #6b7280)' }}>{sublabel}</p>}
        </div>
        <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)}
          className="relative flex-shrink-0 w-[51px] h-[31px] rounded-full transition-all duration-200 active:scale-95"
          style={{ background: checked ? (danger ? 'var(--accent-danger,#ef4444)' : 'var(--accent-primary,#3b82f6)') : 'var(--bg-tertiary,#d1d5db)' }}>
          <span className="absolute top-[2px] left-[2px] w-[27px] h-[27px] rounded-full bg-white shadow transition-transform duration-200"
            style={{ transform: checked ? 'translateX(20px)' : 'translateX(0)' }} />
        </button>
      </div>
      <label className="ov-checkbox-row flex items-center gap-3 cursor-pointer p-3 rounded-lg hover:bg-[var(--bg-tertiary)] transition-colors">
        <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
          className="w-5 h-5 rounded border-2 border-[var(--border-color)]"
          style={{ accentColor: danger ? 'var(--accent-danger)' : 'var(--accent-primary)' }} />
        <div>
          <span className="text-sm font-medium block"
            style={{ color: danger ? 'var(--accent-danger)' : 'var(--text-primary)' }}>
            {label}
          </span>
          {sublabel && <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{sublabel}</span>}
        </div>
      </label>
    </>
  );
}

function Section({ icon: Icon, title, accentColor = 'var(--accent-primary, #3b82f6)', children }) {
  return (
    <div className="rounded-2xl overflow-hidden mb-4"
      style={{ background: 'var(--bg-secondary, #f9fafb)', border: '1px solid var(--border-color, #e5e7eb)' }}>
      <div className="flex items-center gap-2 px-4 py-3"
        style={{ borderBottom: '1px solid var(--border-color, #e5e7eb)' }}>
        <Icon className="w-4 h-4" style={{ color: accentColor }} />
        <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-secondary, #6b7280)' }}>
          {title}
        </span>
      </div>
      <div className="px-4 divide-y" style={{ borderColor: 'var(--border-color, #e5e7eb)' }}>
        {children}
      </div>
    </div>
  );
}

function mapOverrideToForm(override) {
  if (!override) return {
    maxLeverage: '', maxLotSize: '', minLotSize: '', lotStep: '',
    marginCallLevel: '', stopOutLevel: '', maxOpenPositions: '',
    commissionPerLot: '', tradingDisabled: 0,
  };
  return {
    maxLeverage:      override.maxLeverage      ?? '',
    maxLotSize:       override.maxLotSize       ?? '',
    minLotSize:       override.minLotSize       ?? '',
    lotStep:          override.lotStep          ?? '',
    marginCallLevel:  override.marginCallLevel  ?? '',
    stopOutLevel:     override.stopOutLevel     ?? '',
    maxOpenPositions: override.maxOpenPositions ?? '',
    commissionPerLot: override.commissionPerLot ?? '',
    tradingDisabled:  override.tradingDisabled  ?? 0,
  };
}

export default function UserOverrideModal({ user, onClose, onSave, existingOverride = null }) {
  const { t } = useLocale();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(() => mapOverrideToForm(existingOverride));

  useEffect(() => { setFormData(mapOverrideToForm(existingOverride)); }, [existingOverride]);

  const set = (f, v) => setFormData(p => ({ ...p, [f]: v }));

  const minAboveMax =
    formData.minLotSize !== '' && formData.maxLotSize !== '' &&
    parseFloat(formData.minLotSize) > parseFloat(formData.maxLotSize);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (minAboveMax) return;
    setLoading(true);
    try {
      await onSave({
        max_leverage:       formData.maxLeverage      === '' ? null : parseInt(formData.maxLeverage),
        max_lot_size:       formData.maxLotSize       === '' ? null : parseFloat(formData.maxLotSize),
        min_lot_size:       formData.minLotSize       === '' ? null : parseFloat(formData.minLotSize),
        lot_step:           formData.lotStep          === '' ? null : parseFloat(formData.lotStep),
        margin_call_level:  formData.marginCallLevel  === '' ? null : parseFloat(formData.marginCallLevel),
        stop_out_level:     formData.stopOutLevel     === '' ? null : parseFloat(formData.stopOutLevel),
        max_open_positions: formData.maxOpenPositions === '' ? null : parseInt(formData.maxOpenPositions),
        commission_per_lot: formData.commissionPerLot === '' ? null : parseFloat(formData.commissionPerLot),
        trading_disabled:   formData.tradingDisabled ? 1 : 0,
      });
      toast.success(t('userOverride.saveSuccess'));
      onClose();
    } catch (err) {
      toast.error(err.message || t('userOverride.saveFailed'));
    } finally {
      setLoading(false);
    }
  };

  const gd = t('userOverride.groupDefault');

  return (
    <>
      <style>{`
        .ov-thumb::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 26px; height: 26px; border-radius: 50%;
          background: var(--accent-primary, #3b82f6);
          box-shadow: 0 1px 4px rgba(0,0,0,.25), 0 0 0 3px color-mix(in srgb, var(--accent-primary,#3b82f6) 18%, transparent);
          border: 2.5px solid #fff; cursor: pointer; transition: transform .1s;
        }
        .ov-thumb:active::-webkit-slider-thumb { transform: scale(1.15); }
        .ov-thumb::-moz-range-thumb {
          width: 26px; height: 26px; border-radius: 50%;
          background: var(--accent-primary, #3b82f6);
          border: none; cursor: pointer;
        }
        /* hide number input spinners */
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        input[type=number] { -moz-appearance: textfield; }
        @media (max-width: 767px) {
          .ov-backdrop { align-items: flex-end; }
          .ov-dialog { width: 100%; max-width: 100%; border-radius: 20px 20px 0 0; max-height: 92dvh; animation: ovSlideUp .28s cubic-bezier(.32,.72,0,1); }
          .ov-handle { display: flex; }
          .ov-footer { padding: 12px 16px; padding-bottom: max(16px, env(safe-area-inset-bottom)); }
          .ov-footer button { padding-top: 14px; padding-bottom: 14px; border-radius: 16px; font-size: 16px; font-weight: 600; }
          .ov-toggle-row { display: flex; }
          .ov-checkbox-row { display: none; }
        }
        @media (min-width: 768px) {
          .ov-backdrop { align-items: center; justify-content: center; padding: 16px; }
          .ov-dialog { width: 100%; max-width: 768px; border-radius: 16px; max-height: 90vh; border: 1px solid var(--border-color); }
          .ov-handle { display: none; }
          .ov-footer { padding: 24px; }
          .ov-footer button { padding: 12px 24px; border-radius: 8px; font-size: 14px; font-weight: 500; }
          .ov-toggle-row { display: none; }
          .ov-checkbox-row { display: flex; }
        }
        @keyframes ovSlideUp {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
      `}</style>

      <div className="ov-backdrop fixed inset-0 z-50 flex"
        style={{ background: 'rgba(0,0,0,.5)', backdropFilter: 'blur(4px)' }}
        onClick={e => e.target === e.currentTarget && onClose()}>

        <div className="ov-dialog flex flex-col overflow-hidden"
          style={{ background: 'var(--bg-primary, #fff)' }}>

          {/* Mobile drag handle */}
          <div className="ov-handle justify-center pt-3 pb-1 flex-shrink-0">
            <div className="w-10 h-[5px] rounded-full" style={{ background: 'var(--border-color, #d1d5db)' }} />
          </div>

          {/* Header */}
          <div className="p-6 flex items-center justify-between flex-shrink-0"
            style={{ borderBottom: '1px solid var(--border-color, #e5e7eb)' }}>
            <div>
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Shield className="w-5 h-5 text-[var(--accent-primary)]" />
                {t('userOverride.title')}
              </h2>
              <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                {user.first_name} {user.last_name} · {user.email}
              </p>
            </div>
            <button onClick={onClose}
              className="p-2 hover:bg-[var(--bg-secondary)] rounded-lg transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-4 py-4"
            style={{ WebkitOverflowScrolling: 'touch' }}>

            {/* Info banner */}
            <div className="p-4 rounded-lg mb-4"
              style={{
                background: 'color-mix(in srgb, var(--accent-info,#0ea5e9) 10%, transparent)',
                border: '1px solid color-mix(in srgb, var(--accent-info,#0ea5e9) 25%, transparent)',
              }}>
              <div className="flex gap-3">
                <Info className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--accent-info,#0ea5e9)' }} />
                <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <strong style={{ color: 'var(--text-primary)' }}>{t('userOverride.infoBannerTitle')}</strong>
                  <br />
                  {t('userOverride.infoBannerDesc')}
                </div>
              </div>
            </div>

            {/* Trading Control */}
            <Section icon={Shield} title={t('userOverride.sectionTradingControl')} accentColor="var(--accent-danger, #ef4444)">
              <ToggleRow
                checked={formData.tradingDisabled === 1}
                onChange={v => set('tradingDisabled', v ? 1 : 0)}
                label={t('userOverride.disableTrading')}
                sublabel={t('userOverride.disableTradingSubLabel')}
                danger
              />
            </Section>

            {/* Risk Parameters */}
            <Section icon={AlertTriangle} title={t('userOverride.sectionRiskParameters')} accentColor="var(--accent-warning,#f59e0b)">
              <SliderField
                label={t('userOverride.maxLeverage')}
                resetLabel={t('userOverride.resetToGroupDefault')}
                value={formData.maxLeverage}
                displayValue={formData.maxLeverage ? `1:${formData.maxLeverage}` : gd}
                sliderValue={formData.maxLeverage || 100} min={1} max={2000} step={1}
                onChange={e => set('maxLeverage', e.target.value)}
                onReset={() => set('maxLeverage', '')}
                onDirectInput={v => set('maxLeverage', String(v))} />
              <SliderField
                label={t('userOverride.maxLotSize')}
                resetLabel={t('userOverride.resetToGroupDefault')}
                value={formData.maxLotSize}
                displayValue={formData.maxLotSize ? `${formData.maxLotSize} ${t('userOverride.lots')}` : gd}
                sliderValue={formData.maxLotSize || 10} min={0.01} max={500} step={0.01}
                onChange={e => set('maxLotSize', e.target.value)}
                onReset={() => set('maxLotSize', '')}
                onDirectInput={v => set('maxLotSize', String(v))} />
              <SliderField
                label={t('userOverride.minLotSize')}
                resetLabel={t('userOverride.resetToGroupDefault')}
                value={formData.minLotSize}
                displayValue={formData.minLotSize ? `${formData.minLotSize} ${t('userOverride.lots')}` : gd}
                sliderValue={formData.minLotSize || 0.01} min={0.01} max={10} step={0.01}
                onChange={e => set('minLotSize', e.target.value)}
                onReset={() => set('minLotSize', '')}
                onDirectInput={v => set('minLotSize', String(v))} />
              <SliderField
                label={t('userOverride.lotStep')}
                resetLabel={t('userOverride.resetToGroupDefault')}
                value={formData.lotStep}
                displayValue={formData.lotStep ? `${formData.lotStep}` : gd}
                sliderValue={formData.lotStep || 0.01} min={0.001} max={1} step={0.001}
                onChange={e => set('lotStep', e.target.value)}
                onReset={() => set('lotStep', '')}
                onDirectInput={v => set('lotStep', String(v))} />
              {minAboveMax && (
                <div className="py-2">
                  <p className="text-xs flex items-center gap-1" style={{ color: 'var(--accent-danger, #ef4444)' }}>
                    {t('userOverride.minAboveMaxError')}
                  </p>
                </div>
              )}
              <SliderField
                label={t('userOverride.marginCallLevel')}
                resetLabel={t('userOverride.resetToGroupDefault')}
                value={formData.marginCallLevel}
                displayValue={formData.marginCallLevel ? `${formData.marginCallLevel}%` : gd}
                sliderValue={formData.marginCallLevel || 80} min={0} max={150} step={5}
                onChange={e => set('marginCallLevel', e.target.value)}
                onReset={() => set('marginCallLevel', '')}
                onDirectInput={v => set('marginCallLevel', String(v))}
                accentVar="--accent-warning, #f59e0b" />
              <SliderField
                label={t('userOverride.stopOutLevel')}
                resetLabel={t('userOverride.resetToGroupDefault')}
                value={formData.stopOutLevel}
                displayValue={formData.stopOutLevel ? `${formData.stopOutLevel}%` : gd}
                sliderValue={formData.stopOutLevel || 50} min={0} max={100} step={5}
                onChange={e => set('stopOutLevel', e.target.value)}
                onReset={() => set('stopOutLevel', '')}
                onDirectInput={v => set('stopOutLevel', String(v))}
                accentVar="--accent-danger, #ef4444" />
              <SliderField
                label={t('userOverride.maxOpenPositions')}
                resetLabel={t('userOverride.resetToGroupDefault')}
                value={formData.maxOpenPositions}
                displayValue={formData.maxOpenPositions || gd}
                sliderValue={formData.maxOpenPositions || 200} min={1} max={500} step={1}
                onChange={e => set('maxOpenPositions', e.target.value)}
                onReset={() => set('maxOpenPositions', '')}
                onDirectInput={v => set('maxOpenPositions', String(v))} />
            </Section>

            {/* Pricing */}
            <Section icon={DollarSign} title={t('userOverride.sectionPricingOverrides')}>
              <SliderField
                label={t('userOverride.commissionPerLot')}
                hint={t('userOverride.commissionPerLotHint')}
                resetLabel={t('userOverride.resetToGroupDefault')}
                value={formData.commissionPerLot}
                displayValue={formData.commissionPerLot ? `$${formData.commissionPerLot}` : gd}
                sliderValue={formData.commissionPerLot || 0} min={0} max={50} step={0.5}
                onChange={e => set('commissionPerLot', e.target.value)}
                onReset={() => set('commissionPerLot', '')}
                onDirectInput={v => set('commissionPerLot', String(v))} />
            </Section>

            {existingOverride && (
              <div className="p-4 rounded-lg mb-4"
                style={{
                  background: 'color-mix(in srgb, var(--accent-warning,#f59e0b) 10%, transparent)',
                  border: '1px solid color-mix(in srgb, var(--accent-warning,#f59e0b) 25%, transparent)',
                }}>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <strong style={{ color: 'var(--text-primary)' }}>{t('userOverride.currentOverrideTitle')}</strong>
                  <br />
                  {t('userOverride.currentOverrideDesc')}
                </p>
              </div>
            )}

            <div className="h-2" />
          </form>

          {/* Footer */}
          <div className="ov-footer flex gap-3 flex-shrink-0"
            style={{ borderTop: '1px solid var(--border-color, #e5e7eb)' }}>
            <button type="button" onClick={onClose} disabled={loading}
              className="flex-1 bg-[var(--bg-secondary)] text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] transition-colors disabled:opacity-50">
              {t('common.cancel')}
            </button>
            <button type="button" onClick={handleSubmit} disabled={loading || minAboveMax}
              className="flex-1 bg-[var(--accent-primary)] text-white hover:bg-[var(--accent-primary-hover)] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {t('userOverride.saving')}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {t('userOverride.saveOverride')}
                </>
              )}
            </button>
          </div>

        </div>
      </div>
    </>
  );
}